const STOREY_TYPE = 'IfcBuildingStorey';

export interface MetaObjectData {
  id: string | number;
  name?: string;
  type?: string;
  parent?: string | number | null;
  children?: number[];
  attributes?: Record<string, unknown>;
}

/* Revit metadata element. A storey is an element with `class === 'Level'`; every other
   element references its level through `Level`, an index into the `Elements` array. */
export interface RvtElementData {
  Id: string | number;
  Name?: string;
  class?: string;
  Level?: number | null;
  /* Mesh names in the XKT payload - the Entity's originalSystemId. Absent in older metadata,
     where the element draws as a single Entity named after its Id. */
  appearances?: string[];
}

export interface ModelMetadata {
  metaObjects?: MetaObjectData[];
  Elements?: RvtElementData[];
}

export interface MetaSceneObject {
  id: string | number;
  name?: string;
  type?: string;
  attributes?: Record<string, unknown>;
  children?: MetaSceneObject[];
}

export interface MetaSceneModel {
  metaObjects?: { [id: string]: MetaSceneObject };
}

export interface MetaNode {
  id: string;
  name: string;
  type?: string;
  attributes?: Record<string, unknown>;
  /* Keys the node's Entities are found under - an Entity's originalSystemId. */
  objectKeys: string[];
  childIds: string[];
  isStorey: boolean;
}

/* Normalizes a SceneModel.metadata metaObjects array (children given as indices, or a parent id). */
export function nodesFromMetadata(metaObjects: MetaObjectData[]): MetaNode[] {
  const nodesById = new Map<string, MetaNode>();
  const nodes = metaObjects.map((metaObject) => {
    const id = String(metaObject.id);
    const node: MetaNode = {
      id,
      name: metaObject.name ?? id,
      type: metaObject.type,
      attributes: metaObject.attributes,
      objectKeys: [id],
      childIds: [],
      isStorey: metaObject.type === STOREY_TYPE,
    };
    nodesById.set(id, node);
    return node;
  });

  metaObjects.forEach((metaObject, index) => {
    const node = nodes[index];
    if (Array.isArray(metaObject.children) && metaObject.children.length) {
      for (const childIndex of metaObject.children) {
        const childMetaObject = metaObjects[childIndex];
        if (childMetaObject) node.childIds.push(String(childMetaObject.id));
      }
    } else if (metaObject.parent !== undefined && metaObject.parent !== null) {
      const parent = nodesById.get(String(metaObject.parent));
      if (parent && parent.id !== node.id) parent.childIds.push(node.id);
    }
  });

  return nodes;
}

/* Normalizes a populated viewer.metaScene MetaModel (children are object references). */
export function nodesFromMetaScene(metaModel: MetaSceneModel): MetaNode[] {
  const metaObjects = metaModel.metaObjects ?? {};
  return Object.values(metaObjects).map((metaObject) => ({
    id: String(metaObject.id),
    name: metaObject.name ?? String(metaObject.id),
    type: metaObject.type,
    attributes: metaObject.attributes,
    objectKeys: [String(metaObject.id)],
    childIds: Array.isArray(metaObject.children)
      ? metaObject.children.map((child) => String(child.id))
      : [],
    isStorey: metaObject.type === STOREY_TYPE,
  }));
}

/* Normalizes Revit `Elements` metadata. Storeys are `Level` elements; every other element
   is attached to its level via the `Level` index, so a level's subtree is all its elements.
   `Elevation` is deliberately ignored: Revit reports it in feet on its own Z-up axis, which
   is not comparable with the World coordinates of the AABBs it would be clipped against. */
export function nodesFromRvtElements(elements: RvtElementData[]): MetaNode[] {
  const nodes: MetaNode[] = elements.map((element) => {
    const id = String(element.Id);
    return {
      id,
      name: element.Name ?? id,
      type: element.class,
      objectKeys: element.appearances?.length ? element.appearances : [id],
      childIds: [],
      isStorey: element.class === 'Level',
    };
  });

  elements.forEach((element, index) => {
    const levelIndex = element.Level;
    if (levelIndex === undefined || levelIndex === null) return;
    const level = nodes[levelIndex];
    const node = nodes[index];
    if (level && level.id !== node.id) level.childIds.push(node.id);
  });

  return nodes;
}

/**
 * In-memory metaObject graph. Source is a SceneModel's runtime metadata when present
 * (models loaded with `loadIntoMetaScene: false`), otherwise the populated viewer.metaScene
 * (models with embedded metadata). Either way storeys no longer depend on a single source.
 */
export class MetaGraph {
  readonly nodes = new Map<string, MetaNode>();
  readonly nodesByObjectKey = new Map<string, MetaNode>();

  constructor(nodes: MetaNode[]) {
    for (const node of nodes) {
      this.nodes.set(node.id, node);
      for (const objectKey of node.objectKeys) this.nodesByObjectKey.set(objectKey, node);
    }
  }

  getSubtreeIds(rootId: string): string[] {
    const result: string[] = [];
    const seen = new Set<string>();
    const stack = [rootId];
    while (stack.length) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const node = this.nodes.get(id);
      if (!node) continue;
      result.push(id);
      for (const childId of node.childIds) stack.push(childId);
    }
    return result;
  }

  getStoreyIds(): string[] {
    const ids: string[] = [];
    for (const node of this.nodes.values()) {
      if (node.isStorey) ids.push(node.id);
    }
    return ids;
  }

  /* Entity keys of a storey and everything under it, in the order the subtree walks. */
  getSubtreeObjectKeys(rootId: string): string[] {
    return this.getSubtreeIds(rootId).flatMap((id) => this.nodes.get(id)?.objectKeys ?? [id]);
  }
}

export function readElevation(node?: MetaNode): number | undefined {
  const raw = node?.attributes?.elevation;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const num = Number(raw);
  return Number.isNaN(num) ? undefined : num;
}
